import { useVlpQuote, generateSyntheticOrderBook } from "@/backend/hooks/use-vlp-quotes";
import { useOrderBookConfig, DEFAULT_ORDER_BOOK_CONFIG } from "@/backend/hooks/use-order-book-config";
import { useOrderForm } from "@/features/order-form/order-form-context";
import { useProduct } from "@/features/product/product-context";
import { MarketTrades } from "@/features/market-trades/market-trades";
import { useSyntheticTrades } from "@/features/market-trades/use-synthetic-trades";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { OrderBook } from "./order-book";
import { TradingPanelCard } from "@/features/shared/trading-panel-card";
import { useMarketPrice } from "@/backend/store";

const defaultMarketSymbol = "BTC-USD";

type OrderBookPanelProps = {
  panelId: string;
};

export const OrderBookPanel = ({ panelId: _panelId }: OrderBookPanelProps) => {
  const { product } = useProduct();
  const { setLimitPrice, setAmount } = useOrderForm();
  const [activeTab, setActiveTab] = useState<"order-book" | "trades">(
    "order-book"
  );
  const showDesktopHeader = useMediaQuery("(min-width: 1001px)");
  
  // Lift tick size state to panel level so we can pass it to synthetic order book generator
  const [tickSize, setTickSize] = useState<number>(0);
  
  // Fetch order book generation config
  // Single source of truth from database (no placeholder switching)
  const { data: orderBookConfig } = useOrderBookConfig();
  
  // Use database config if loaded, otherwise fallback to code default
  // This only happens once on initial load, then stays stable
  const generationConfig = useMemo(
    () => {
      if (!orderBookConfig) {
        return DEFAULT_ORDER_BOOK_CONFIG.generation;
      }
      return orderBookConfig.generation;
    },
    [orderBookConfig]
  );

  useEffect(() => {
    if (!showDesktopHeader && activeTab !== "order-book") {
      setActiveTab("order-book");
    }
  }, [showDesktopHeader, activeTab]);
  
  // CRITICAL: Reset tick size when market changes to prevent state pollution
  useEffect(() => {
    setTickSize(0); // Reset to default when product changes
  }, [product?.id]);
  
  // Get market symbol for VLP (e.g., "BTC-USD")
  const vlpMarket = product?.id;
  
  // Fetch VLP quote for order book data
  // NOTE: Testing without throttle - the pure generation function with animation clock
  // should handle rate limiting. If this causes issues, restore throttling.
  const { rawQuote, isLoading: vlpLoading } = useVlpQuote(vlpMarket);

  // Get market price from Zustand store (real-time WebSocket updates)
  // This is the mark price from the configured provider (CoinAPI/Massive)
  const currentPrice = useMarketPrice(product?.id || defaultMarketSymbol);
  
  const isLoading = vlpLoading || !rawQuote;
  
  // Use synthetic trades based on VLP data (matches order book activity)
  const { trades: syntheticTrades, isLoading: tradesLoading } = useSyntheticTrades({
    quote: rawQuote ?? null,
    maxTrades: 100, // Generate enough for any screen size
    avgTradeInterval: 600, // Average ~600ms between trades (active market feel)
    basePrecision: product?.base.precision ?? 8,
    quotePrecision: product?.quote.precision ?? 2,
  });
  const memoizedTrades = useMemo(() => syntheticTrades ?? [], [syntheticTrades]);
  const { height, callbackRef } = useElementHeight();

  const maxNumLevels = useMemo(() => {
    if (height === 0) {
      return 10; // Default
    }

    // Subtract space for header (24px) and buy/sell ratio bar (20px)
    const availableHeight = Math.max(height - 24 - 20, 0);
    const totalLevels = Math.floor(availableHeight / 24);

    // Split between asks and bids
    return Math.max(Math.floor(totalLevels / 2), 5);
  }, [height]);

  // Animation clock - drives order book "life" at controlled intervals
  // This is the industry-standard approach: external clock controls animation frequency
  // Makes the generation function PURE (same inputs = same output) for proper memoization
  // NOW CONFIGURABLE VIA ADMIN PANEL!
  const [animationTimestamp, setAnimationTimestamp] = useState(Date.now());
  
  // Use animation_interval_ms from admin config (falls back to 150ms default if not available)
  const animationIntervalMs = generationConfig?.animation_interval_ms ?? 150;
  
  useEffect(() => {
    // Update animation timestamp at controlled rate from admin config
    // This creates the "alive" feel while preventing excessive re-renders
    const interval = setInterval(() => {
      setAnimationTimestamp(Date.now());
    }, animationIntervalMs);
    
    return () => clearInterval(interval);
  }, [animationIntervalMs]);

  // Generate synthetic order book from VLP quote
  // Memoize asks and bids directly to prevent creating new array references
  // This is critical for React.memo to work properly on child components
  const { asks, bids } = useMemo(() => {
    const book = generateSyntheticOrderBook(
      rawQuote ?? null,
      maxNumLevels,
      product?.base.precision ?? 8,
      product?.quote.precision ?? 2,
      tickSize,
      generationConfig,
      currentPrice,
      animationTimestamp  // External clock - updates every 150ms
    );
    
    // Slice and return in single memo to avoid double memoization issues
    return {
      asks: book.asks.slice(0, Math.max(0, maxNumLevels - 2)),
      bids: book.bids.slice(0, Math.max(0, maxNumLevels - 2)),
    };
  }, [rawQuote, maxNumLevels, product?.base.precision, product?.quote.precision, tickSize, generationConfig, currentPrice, animationTimestamp]);
  
  // Handle tick size changes from OrderBook component
  const handleTickSizeChange = useCallback((newTickSize: number) => {
    setTickSize(newTickSize);
  }, []);

  const onPriceClick = useCallback(
    (value: number) => {
      setLimitPrice(value.toString());
    },
    [setLimitPrice]
  );

  const onAmountClick = useCallback(
    (value: number) => {
      setAmount(value.toString());
    },
    [setAmount]
  );

  if (!product) {
    return null;
  }

  const tabs = [
    { id: "order-book" as const, label: "Order Book" },
    { id: "trades" as const, label: "Trades" },
  ];

  return (
    <TradingPanelCard noPadding data-tour="order-book">
      <div className="flex h-full w-full flex-col">
        {showDesktopHeader && (
          <div className="flex items-center border-b border-border/40 rounded-t-lg">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "relative flex-1 px-4 py-3 text-xs font-normal transition-colors whitespace-nowrap cursor-pointer text-center",
                  activeTab === tab.id
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                )}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 min-h-0">
          {activeTab === "order-book" ? (
            <div ref={callbackRef} className="h-full w-full">
              <OrderBook
                asks={asks}
                bids={bids}
                base={product.base}
                quote={product.quote}
                currentPrice={currentPrice}
                onPriceClick={onPriceClick}
                onAmountClick={onAmountClick}
                isLoading={isLoading}
                tickSize={tickSize}
                onTickSizeChange={handleTickSizeChange}
                vlpQuote={rawQuote}
                marketTickSize={parseFloat(product.quote.tickSize) || 0.01}
              />
            </div>
          ) : (
            <div className="flex h-full flex-col">
              <MarketTrades
                trades={memoizedTrades}
                base={product.base}
                quote={product.quote}
                isLoading={tradesLoading}
              />
            </div>
          )}
        </div>
      </div>
    </TradingPanelCard>
  );
};

const useElementHeight = () => {
  const [height, setHeight] = useState(0);

  const updateHeight = useCallback<ResizeObserverCallback>((entries) => {
    const el = entries[0].target;
    setHeight(el.clientHeight);
  }, []);

  const callbackRef = useCallback(
    (el: HTMLElement | null) => {
      if (!el) {
        return;
      }

      const resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(el);

      // https://react.dev/blog/2024/12/05/react-19#cleanup-functions-for-refs
      return () => {
        resizeObserver.disconnect();
      };
    },
    [updateHeight]
  );

  return useMemo(() => ({ height, callbackRef }), [height, callbackRef]);
};
